import React, { useState } from 'react';
import MultiFileSelector from '../forms/MultiFileSelector';
import Button from './Button';
import ModalTitle from './ModalTitle';
import FooterBar from '../structure/FooterBar';
import HorizontalSeparator from '../structure/HorizontalSeparator';
import ModalLayout from '../structure/ModalLayout';
import PageContainer from '../structure/PageContainer';
import FullScreenLayout from '../../scenes/fullScreenLayout';
import { color_text_default } from '../../constants/colors';
import styled from '@emotion/styled';
import { style_border_default } from '../../constants/stylesValues';
import { breakpoint_small } from '../../constants/breakpoints';

const closeIcon = require('../../content/images/close_icon.svg');

interface SimpleFileImportDialogProps {
    onClose: () => void;
    completeImport: (file: File) => void;
    title: string;
    acceptFileTypes: string;
    acceptFileTypesDisplayText: string;
    isLoading: boolean;
    errors: string[];
    maxSizeOverrideMegabytes?: number
}

const SimpleFileImportDialog: React.FC<SimpleFileImportDialogProps> = (props) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const handleFilesSelected = (files: File[]) => {
        if (files.length > 0) {
            setSelectedFile(files[0]);
        }
    };

    const handleImportClick = () => {
        if (selectedFile) {
            props.completeImport(selectedFile);
        }
    };

    return (
        <SimpleFileImportDialogStyled
            isVisible={true}
            onClose={props.onClose}>
            <FullScreenLayout>
                <PageContainer fill>
                    <TitleWrapper>
                        <ModalTitle>{props.title}</ModalTitle>
                        <ButtonWrapper>
                            <Button
                                themes={['end-tight', 'icon']}
                                icon={closeIcon}
                                onClick={props.onClose}
                            />
                        </ButtonWrapper>
                    </TitleWrapper>
                    <HorizontalSeparator />

                    <div className="fileSelector">
                        <MultiFileSelector
                            maxSizeOverrideMegabytes={props.maxSizeOverrideMegabytes}
                            onSelected={handleFilesSelected}
                            accept={props.acceptFileTypes}
                            uploadButtonText={"Select a local file"}
                            uploadLabel={"Drag a file here"}
                        />
                        <div className="fileNameRow">
                            <div className="fileName">{selectedFile?.name || ''}</div>
                            <div className="fileTypes">{props.acceptFileTypesDisplayText}</div>
                        </div>
                    </div>

                    <CustomFooter className="import-footer">
                        <Button
                            className="submitButton"
                            type="button"
                            themes={['primary']}
                            text="Import"
                            disabled={props.isLoading || !selectedFile}
                            loading={props.isLoading}
                            onClick={handleImportClick}
                        />
                        <div className="selected-count"></div>
                        <Button
                            type="button"
                            themes={['secondary']}
                            text="Cancel"
                            onClick={props.onClose}
                            disabled={props.isLoading}
                        />
                    </CustomFooter>
                </PageContainer>
            </FullScreenLayout>
        </SimpleFileImportDialogStyled>
    );
};

const ButtonWrapper = styled.div`
    border-left: ${style_border_default};
    padding: 0 0 0 32px;
    margin: 8px 0;
`;

const TitleWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 0 32px;
    justify-content: space-between;
`;

const SimpleFileImportDialogStyled = styled(ModalLayout)`
    color: ${color_text_default};
    height: 590px;
    width: 750px;
    min-width: 30%;
    min-height: 30%;
    ${breakpoint_small} {
        height: calc(100vh - 100px);
    }
    .fileSelector {
        margin: 32px;
        ${breakpoint_small} {
            margin: 16px;
        }
    }
    .fileNameRow {
        margin: 8px;
        margin-bottom: 0px;
        display: flex;
        flex-direction: row;
        .fileTypes {
            font-style: italic;
            margin-left: auto;
        }
        .fileName {
            font-weight: bold;
        }
    }

    .selected-count {
        font-style: normal;
        font-weight: 600;
        font-size: 16px;
        line-height: 24px;
        margin-right: auto;
    }
`;

const CustomFooter = styled(FooterBar)`
    z-index: auto;
`;

export default SimpleFileImportDialog;
